<template>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <Toast />
                <div class="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                        <h5 class="m-0">Usuarios del negocio</h5>
                        <small class="text-500">Usuarios con acceso a tu cuenta. El dueño define el rol y qué puede ver cada uno.</small>
                    </div>
                    <Button label="Nuevo usuario" icon="pi pi-user-plus" :disabled="!canAdd" @click="openCreate" />
                </div>

                <div class="mb-3">
                    <Tag :severity="canAdd ? 'success' : 'warning'" :value="seatLabel" />
                </div>

                <DataTable :value="subUsers" :loading="loading" responsiveLayout="scroll" dataKey="userId">
                    <template #empty>Aún no hay operadores. Crea el primero con “Nuevo usuario”.</template>
                    <Column field="username" header="Usuario" />
                    <Column header="Rol" :style="{width:'140px'}">
                        <template #body="{ data }">
                            <Tag :severity="data.role === 'Administrador' ? 'info' : 'warning'" :value="data.role || 'Operador'" />
                        </template>
                    </Column>
                    <Column header="Estado" :style="{width:'140px'}">
                        <template #body="{ data }">
                            <Tag :severity="statusOf(data).severity" :value="statusOf(data).label" />
                            <small v-if="autoTransactionsOf(data)" class="block mt-1" style="color:#666;">{{ autoTransactionsOf(data) }}</small>
                        </template>
                    </Column>
                    <Column header="Permisos">
                        <template #body="{ data }">{{ permissionSummary(data) }}</template>
                    </Column>
                    <Column header="Acciones" :style="{width:'160px'}">
                        <template #body="{ data }">
                            <Button icon="pi pi-pencil" class="p-button-text p-button-sm mr-1" v-tooltip.bottom="'Editar permisos'" @click="openEdit(data)" />
                            <Button v-if="statusOf(data).canToggle" :icon="data.suspended ? 'pi pi-play' : 'pi pi-ban'"
                                    :class="['p-button-text', 'p-button-sm', data.suspended ? 'p-button-success' : 'p-button-warning']"
                                    v-tooltip.bottom="data.suspended ? 'Reactivar' : 'Suspender'"
                                    @click="toggleSuspend(data)" />
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>

        <Dialog v-model:visible="dialog" :header="editing ? 'Editar usuario' : 'Nuevo usuario'" :modal="true"
                :style="{width:'480px'}" :breakpoints="{'640px':'92vw'}">
            <Message v-if="formError" severity="error" :closable="false">{{ formError }}</Message>

            <div class="field mt-2" v-if="!editing">
                <label>Correo (usuario)</label>
                <InputText v-model="form.username" class="w-full" placeholder="operador@tuempresa.com" autocomplete="off" />
            </div>
            <div class="field" v-else>
                <label class="text-500">Usuario</label>
                <div class="font-medium">{{ form.username }}</div>
            </div>

            <div class="field" v-if="!editing">
                <label>Contraseña</label>
                <Password v-model="form.password" :feedback="true" toggleMask inputClass="w-full" class="w-full" />
            </div>

            <div class="field" v-if="!editing">
                <label class="block mb-1">Rol</label>
                <SelectButton v-model="form.role" :options="roleOptions" optionLabel="label" optionValue="value" :allowEmpty="false" />
                <small class="block text-500 mt-1">{{ form.role === 'Administrador'
                    ? 'Acceso completo del negocio (puede recibir todos los permisos).'
                    : 'Acceso operativo; el dueño elige qué puede ver.' }}</small>
            </div>
            <div class="field" v-else>
                <label class="text-500">Rol</label>
                <div class="font-medium">{{ form.role }}</div>
            </div>

            <div class="field">
                <div class="flex align-items-center">
                    <Checkbox inputId="allperms" v-model="form.allPermissions" :binary="true" />
                    <label for="allperms" class="ml-2">Todos los permisos {{ form.role === 'Administrador' ? 'de administrador' : 'de operador' }}</label>
                </div>
                <small class="text-500">Desactiva para elegir permisos específicos.</small>
            </div>

            <div v-if="!form.allPermissions" class="field">
                <div v-for="(codes, module) in grantableByModule" :key="module" class="mb-2">
                    <div class="font-medium text-600 mb-1">{{ module }}</div>
                    <div v-for="g in codes" :key="g.code" class="flex align-items-center mb-1">
                        <span class="flex align-items-center"
                              v-tooltip.right="lockedCodes[g.code] ? lockedCodes[g.code] + ' lo necesita' : null">
                            <Checkbox :inputId="'p_' + g.code" :value="g.code" v-model="form.selectedCodes"
                                      :disabled="!!lockedCodes[g.code]" @change="applyDependencies" />
                            <label :for="'p_' + g.code" class="ml-2">{{ g.label }}</label>
                        </span>
                    </div>
                </div>
                <small v-if="!currentGrantable.length" class="text-500">No hay permisos asignables.</small>
            </div>

            <template #footer>
                <Button label="Cancelar" class="p-button-text" @click="dialog = false" />
                <Button :label="editing ? 'Guardar' : 'Crear'" icon="pi pi-check" :loading="saving" @click="save" />
            </template>
        </Dialog>
    </div>
</template>

<script>
import httpRequest from '../../service/httpRequest';
import { subUserStatus, autoTransactionsLabel } from '../../service/subUserStatus';
import { applyDependencies } from '../../service/permissionDependencies';

export default {
    name: 'SubUsers',
    data() {
        return {
            subUsers: [],
            ownerAutoTransactions: null,   // «Reportar transacciones» del dueño (H23)
            grantable: [],                 // compat: catálogo de operador
            grantableByRole: {},           // { Operador: [...], Administrador: [...] }
            requires: {},                  // {código: [lecturas que necesita]} (H24)
            unitLabels: {},                // {código: unidad que lo pide}
            roleOptions: [
                { label: 'Operador', value: 'Operador' },
                { label: 'Administrador', value: 'Administrador' },
            ],
            seatLimit: null,
            seatCount: 0,
            seatsAvailable: 0,
            loading: false,
            saving: false,
            dialog: false,
            editing: null,        // userId en edición, o null al crear
            formError: null,
            form: { username: '', password: '', role: 'Operador', allPermissions: true, selectedCodes: [] },
        };
    },
    created() {
        this.load();
    },
    watch: {
        // Al cambiar el rol, descarta los códigos que ya no son asignables a ese rol
        // (p. ej. admin-only al pasar a Operador); el backend igual los filtra.
        'form.role'() {
            const valid = new Set(this.currentGrantable.map((g) => g.code));
            this.form.selectedCodes = this.form.selectedCodes.filter((c) => valid.has(c));
            this.applyDependencies();
        },
    },
    computed: {
        canAdd() {
            return this.seatLimit === null || this.seatsAvailable > 0;
        },
        seatLabel() {
            const lim = this.seatLimit === null ? '∞' : this.seatLimit;
            const avail = this.seatLimit === null ? '∞' : this.seatsAvailable;
            return `Asientos: ${this.seatCount} / ${lim} · disponibles: ${avail}`;
        },
        // Permisos asignables según el rol elegido (Administrador = todo el techo del dueño;
        // Operador = subconjunto elegible). Cae al catálogo de operador por compatibilidad.
        currentGrantable() {
            return (this.grantableByRole && this.grantableByRole[this.form.role]) || this.grantable;
        },
        // Lo que otro permiso marcado necesita queda marcado y bloqueado (H24).
        lockedCodes() {
            return applyDependencies(this.form.selectedCodes, this.requires, this.unitLabels, this.currentGrantable).locked;
        },
        grantableByModule() {
            const out = {};
            for (const g of this.currentGrantable) {
                (out[g.module] = out[g.module] || []).push(g);
            }
            return out;
        },
    },
    methods: {
        statusOf(sub) {
            return subUserStatus(sub);
        },
        async load() {
            this.loading = true;
            const r = await httpRequest.listSubUsers();
            if (r && r.status === 200 && r.data) {
                this.subUsers = r.data.subUsers || [];
                this.grantable = r.data.grantable || [];
                this.grantableByRole = r.data.grantableByRole || {};
                this.seatLimit = r.data.seatLimit;
                this.seatCount = r.data.seatCount;
                this.seatsAvailable = r.data.seatsAvailable;
                this.ownerAutoTransactions = r.data.ownerAutoTransactions;
                this.requires = r.data.requires || {};
                this.unitLabels = r.data.unitLabels || {};
            }
            this.loading = false;
        },
        applyDependencies() {
            this.form.selectedCodes = applyDependencies(
                this.form.selectedCodes, this.requires, this.unitLabels, this.currentGrantable).codes;
        },
        autoTransactionsOf(sub) {
            return autoTransactionsLabel(sub, this.ownerAutoTransactions);
        },
        openCreate() {
            this.editing = null;
            this.formError = null;
            this.form = { username: '', password: '', role: 'Operador', allPermissions: true, selectedCodes: [] };
            this.dialog = true;
        },
        openEdit(sub) {
            this.editing = sub.userId;
            this.formError = null;
            const hasSelection = Array.isArray(sub.permissionSelection);
            this.form = {
                username: sub.username,
                password: '',
                role: sub.role || 'Operador',
                allPermissions: !hasSelection,
                selectedCodes: hasSelection ? [...sub.permissionSelection] : [],
            };
            this.applyDependencies();
            this.dialog = true;
        },
        permissionSummary(sub) {
            const role = sub.role === 'Administrador' ? 'administrador' : 'operador';
            if (!Array.isArray(sub.permissionSelection)) return `Todos los de ${role}`;
            return `${sub.permissionSelection.length} permiso(s)`;
        },
        async save() {
            this.formError = null;
            const selection = this.form.allPermissions ? null : this.form.selectedCodes;
            this.saving = true;
            let r;
            if (this.editing) {
                r = await httpRequest.updateSubUser(this.editing, { permissionSelection: selection });
            } else {
                if (!this.form.username || !this.form.password) {
                    this.formError = 'Correo y contraseña son obligatorios.';
                    this.saving = false;
                    return;
                }
                r = await httpRequest.createSubUser(this.form.username.trim(), this.form.password, selection, this.form.role);
            }
            this.saving = false;
            if (r && r.status === 200) {
                this.$toast.add({ severity: 'success', summary: 'Listo',
                    detail: this.editing ? 'Usuario actualizado' : 'Usuario creado', life: 3000 });
                // Lo que el backend concedió por dependencia (H24), p. ej. «Se concedió
                // Productos (lectura) porque Ventas lo necesita».
                for (const warning of ((r.data && r.data.warnings) || [])) {
                    this.$toast.add({ severity: 'info', summary: 'Permisos', detail: warning, life: 6000 });
                }
                this.dialog = false;
                this.load();
            } else {
                // 403 (SeatError/contraseña) trae el motivo en `error`; el interceptor global ya
                // muestra un toast, pero lo repetimos inline en el diálogo para que quede claro.
                this.formError = (r && r.error) || 'No se pudo guardar el usuario.';
            }
        },
        async toggleSuspend(sub) {
            const r = await httpRequest.updateSubUser(sub.userId, { suspended: !sub.suspended });
            if (r && r.status === 200) {
                this.$toast.add({ severity: 'success', summary: 'Listo',
                    detail: sub.suspended ? 'Usuario reactivado' : 'Usuario suspendido', life: 3000 });
                this.load();
            } else {
                this.$toast.add({ severity: 'error', summary: 'Error',
                    detail: (r && r.error) || 'No se pudo cambiar el estado', life: 4000 });
            }
        },
    },
};
</script>
